import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { apiResponsesSelector, quoteFormSelector } from 'ctm-selectors';
import { transforms, pageContext } from 'ctm-util-functions';

export * from 'ctm-selectors/selectors/state';

/**
 * Basic selector to get product category string instead of code, normally used for tracking
 * @param {Immutable.Map} state - The application state
 * @returns {String} Product Category text
 */
export const getProductCategory = (state) => {
    switch (coverType(state)) {
        case 'COMPREHENSIVE':
            return 'Comprehensive';
        case 'TPFT':
            return 'Third Party Fire and Theft';
        case 'TPPD':
            return 'Third Party Property Damage';
        default:
            return '';
    }
};

/**
 * Generic selector to get a form value
 * @param {Immutable.Map} state - The application state
 * @param {String} identifier selector identifier
 * @returns {any} returns the form value
 */
export const getFormValue = (state, identifier) => quoteFormSelector(state).getIn(identifier.split('.'));
/**
 * Selector to get selected make if the make is popular
 * @param {Immutable.Map} state - The application state
 * @returns {String} The car make code or empty string if car make is not popular
 */
export const getSelectedMakeIfPopular = (state) => {
    const selectedMake = quoteFormSelector(state).getIn(['coverDetail', 'vehicle', 'make']);
    const makes = apiResponsesSelector(state).get('makes') || Immutable.List();
    const isSelectedMakePopular = !makes.filter((make) => make.get('code') === selectedMake && make.get('popular') === true).isEmpty();
    return isSelectedMakePopular ? selectedMake : '';
};

/**
 * Get the parking address as a label
 * @param {Object} args - The args
 * @param {Immutable.Map} args.state - The application Redux store.
 * @returns {String} The overnight parking address
 */
export const parkingAddress = ({ state }) => {
    const value = quoteFormSelector(state).getIn(['coverDetail', 'overnightParking', 'address', 'streetName']);
    return transforms.titleCase({ value });
};

/**
 * Get the Vehicle Make as a label
 * @param {Object} args - The args
 * @param {Immutable.Map} args.state - The application Redux store.
 * @returns {String} A vehicle make label
 */
export const vehicleMake = ({ state }) => {
    return getLabelFromCode(state, 'coverDetail.vehicle.make', 'vertical.quote.apiResponses.makes');
};

/**
 * Get the Vehicle Year
 * @param {Immutable.Map} args.state - The application Redux store.
 * @returns {String} A vehicle make label
 */
export const vehicleYear = ({ state }) => {
    return getFormValue(state, 'coverDetail.vehicle.year');
};

/**
 * Get the Vehicle Model
 * @param {Immutable.Map} args.state - The application Redux store.
 * @returns {String} A vehicle make label
 */
export const vehicleModel = ({ state }) => {
    return getFormValue(state, 'coverDetail.vehicle.model');
};

/**
 * Gets the colour label
 * Currently the colour codes are just uppercase version of the colour label
 * assuming nothing changes this functionality should be fine
 * @param {Immutable.Map} state - The application state
 * @returns {String} A vehicle colour label
 */
export const getColourLabel = (state) => {
    const value = quoteFormSelector(state).getIn(['coverDetail', 'vehicle', 'colour']);
    return transforms.titleCase({ value });
};

/**
 * Gets the Label key from the code key
 * @param {Immutable.Map} state - The application Redux store.
 * @param {String} formKey - The formKey for the code
 * @param {String} stateKey - the stateKey for the payload
 * @returns {String} A label from codeKey
 */
export const getLabelFromCode = (state, formKey, stateKey) => {
    // example formKey = 'vehicle.make'
    const code = quoteFormSelector(state).getIn(formKey.split('.'));
    if (!state.getIn(stateKey.split('.'))) return '';
    // example stateKey = 'vertical.quote.apiResponses.makes'
    const objectFromCode = state.getIn(stateKey.split('.')).find((obj) => {
        // sometimes obj might be null
        // added this check to prevent exception being thrown
        if (Immutable.Map.isMap(obj)) {
            return obj.get('code') === code;
        }
        return obj;
    });
    return objectFromCode ? objectFromCode.get('label') : '';
};
/**
 * Get the Users first name
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {String} Drivers first name
 */
export const applicantFirstName = ({ state }) => {
    const firstName = quoteFormSelector(state).getIn(['applicant', 'firstName']);
    return firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : '';
};

/**
 * Check if application is the regularDriver
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {Boolean} True if the application is the regularDrive, false otherwise
 */
export const isApplicantRegularDriver = (state) => {
    return quoteFormSelector(state).getIn(['applicant', 'regularDriver']) === 'Y';
};

/**
 * Return Your or Their String based on isRegularDriver
 * @param {Object} args.state - The app state
 * @returns {String} Your/Their string
 */
export const isRegularDriverYourTheirText = ({ state }) => {
    return isApplicantRegularDriver(state) ? 'your' : 'their';
};

/**
 * Return Your or The regular driver String based on isRegularDriver
 * @param {Object} args.state - The app state
 * @returns {String} You/The Regular Driver string
 */
export const isRegularDriverYouTheRegText = ({ state }) => {
    return isApplicantRegularDriver(state) ? 'you' : 'the regular driver';
};

/**
 * Return Your or The regular drivers String based on isRegularDriver
 * @param {Object} args.state - The app state
 * @returns {String} Your/The Regular Drivers string
 */
export const isRegularDriverYourTheRegDriversText = ({ state }) => {
    return isApplicantRegularDriver(state) ? 'your' : 'the regular driver\'s';
};

/**
 * Return Do or Does String based on isRegularDriver
 * @param {Object} args.state - The app state
 * @returns {String} Do/Does string
 */
export const isRegularDriverDoDoesText = ({ state }) => {
    return isApplicantRegularDriver(state) ? 'do' : 'does';
};

/**
 * Return Have or Has String based on isRegularDriver
 * @param {Object} args.state - The app state
 * @returns {String} Have/Has string
 */
export const isRegularDriverHaveHasText = ({ state }) => {
    const newRegularDriverClaims = pageContext.getProps().pageState.serverConfig.get('newRegularDriverClaims');

    if (newRegularDriverClaims) {
        return isApplicantRegularDriver(state) ? 'have' : 'has';
    }

    return isApplicantRegularDriver(state) ? 'Have' : 'Has';
};

/**
 * Return FirstName String based on isRegularDriver
 * @param {Object} args.state - The app state
 * @returns {String} FirstName string
 */
export const isRegularDriverFirstName = ({ state }) => {
    return isApplicantRegularDriver(state) ? applicantFirstName({ state }) : '';
};

/**
 * This is a Car specific method to compare the person's age and the licenceAge entered, to see if the NCD they selected is valid.
 * The rule this is used in is isNCDYearsLTEYearsDriving
 * NOTE: This method should really be in some kind of file called state_transforms.js, that is exported from the App, in the same way state_selectors is as verticalSelectors.
 * As we only have the one use case at the moment, I've decided to leave it in here, to reduce that overhead.
 * @param {Object} args - The args
 * @param {Object} args.state - The app state
 * @param {Date} mockToday - The mock age passed in from unit tests
 * @returns {Number} The difference between the driver's age and the license age entered.
 */
export const getDifferenceBetweenAgeAndLicenceAge = ({ state }, mockToday = null) => {
    const form = quoteFormSelector(state);
    const actionValue = 'vertical.quote.form.coverDetail.driver.dob';
    const driverAge = transforms.age({ state, actionValue }, mockToday);
    if (isNaN(driverAge)) {
        return 0;
    }
    const driverLicenceAge = parseFloat(form.getIn(['coverDetail', 'driver', 'licenceAge']));
    if (isNaN(driverLicenceAge)) {
        return 0;
    }
    return driverAge - driverLicenceAge;
};

/**
 * Selector to get factory options from apiResponses
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {Immutable.List} - list of factory options
 */
export const factoryOptions = (state) => {
    return apiResponsesSelector(state).get('factoryOptions') || Immutable.List();
};

/**
 * Selector to get selected factory options' code list
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {Immutable.List} - list of selected factory option codes
 */
export const selectedFactoryOptions = (state) => {
    return quoteFormSelector(state).getIn(['coverDetail', 'vehicle', 'factoryOptions']) || Immutable.List();
};

/**
 * Selector to get standardFeatures from apiResponses
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {Immutable.List} - list of standard features
 */
export const standardFeatures = (state) => {
    return apiResponsesSelector(state).get('standardFeatures') || Immutable.List();
};

/**
 * Selector to get nonStandardAccessories from apiResponses
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {Immutable.List} - list of non-standard accessories
 */
export const nonStandardAccessories = (state) => {
    return apiResponsesSelector(state).get('nonStandardAccessories') || Immutable.List();
};

/**
 * Selector to get providers from apiResponses
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {Immutable.List} - list of providers
 */
export const previousInsurers = (state) => {
    return apiResponsesSelector(state).get('previousInsurers') || Immutable.List();
};

/**
 * Selector to get selected nonStandardAccessories from quote form, each item only contains code and price
 * @param {Immutable.Map} state - The application Redux store.
 * @returns {Immutable.List} - list of non-standard accessories
 */
export const selectedNonStandardAccessories = (state) => {
    return quoteFormSelector(state).getIn(['coverDetail', 'vehicle', 'nonStandardAccessories']) || Immutable.List();
};

/**
 * Reselect selector to get nonStandardAccessories drop-down options. Need to filter the ones already selected
 * @returns {Array} - list of non-standard accessories to render the drop-down
 */
export const nonStandardAccessoriesDropDownOptions = createSelector(
    [nonStandardAccessories, selectedNonStandardAccessories],
    (nonStdAccessories, selectedNonStdAccessories) => {
        return nonStdAccessories.toJS().filter((accessory) => selectedNonStdAccessories.toJS().filter((item) => item.code === accessory.code).length <= 0);
    }
);

/**
 * Reselect selector to get nonStandardAccessories drop-down options. Need to filter the ones already selected
 * @returns {Array} - list of non-standard accessories to render the drop-down
 */
export const previousInsurersDropDownOptions = createSelector(
    [previousInsurers],
    (prevInsurers) => {
        return prevInsurers.toJS();
    }
);

/**
 * Reselect selector to get selected nonStandardAccessories that include priceOptions to render the list
 * @returns {Array} - list of non-standard accessories to render the selected list in page
 */
export const selectedNonStandardAccessoriesDetails = createSelector(
    [nonStandardAccessories, selectedNonStandardAccessories],
    (nonStdAccessories, selectedNonStdAccessories) => {
        // This is to fix an issue when trying to reload an existing journey, this state selector tries to read
        // from the nonStandardAccessories state which hasn't been set yet as it is relying on the API response
        // Once the API response comes back, the component is rerendered and it works as expected
        if (nonStdAccessories.size === 0) {
            return [];
        }

        return selectedNonStdAccessories.toJS().map((accessory) => {
            const accessoryDetails = nonStdAccessories.toJS().filter((item) => item.code === accessory.code)[0];
            const { label } = accessoryDetails;
            return {
                ...accessory,
                label
            };
        });
    }
);

/**
 * Selector for getting the factoryOptionsText based on if any options are selected
 * @param {Immutable.Map} state The application state
 * @returns {String} The factor options text
 */
export const factoryOptionsButtonText = ({ state }) => {
    const noSelectedFactoryOptions = selectedFactoryOptions(state).isEmpty();
    return noSelectedFactoryOptions ? 'No Factory options' : 'Next';
};

export const nonStdAccessoriesButtonText = ({ state }) => {
    const noNonStdAccessories = selectedNonStandardAccessories(state).isEmpty();
    return noNonStdAccessories ? 'No non-standard accessories' : 'Next';
};

/**
 *
 * @param {Immutable.Map} state - The application state
 * @param {String} redbookCode - the unique code to filter vehicle data from the list
 * @returns {Immutable.Map} The filtered/Selected vehicle data
 */
export const selectedVehicleData = (state, redbookCode) => {
    const allCarSeries = apiResponsesSelector(state).get('series');

    if (Immutable.List.isList(allCarSeries)) {
        return allCarSeries.filter((vehicle) => vehicle.get('redbookCode') === redbookCode).get(0) || Immutable.Map();
    }

    return Immutable.Map();
};

export const requestedExcess = (state) => {
    return quoteFormSelector(state).getIn(['coverDetail', 'requestedExcess']);
};

export const coverType = (state) => {
    return quoteFormSelector(state).getIn(['coverDetail', 'coverType']);
};

/**
 * Updates the vehicle make label for the tracking form payload
 * @param {Immutable.Map} state - The application state
 * @param {Immutable.Map} formPayload - the form payload
 * @returns {Immutable.Map} The transformed form payload
 */
export const verticalTrackingFormPayload = ({ formPayload, state }) => {
    const carMakeStorePath = ['coverDetail', 'vehicle', 'make'];
    if (formPayload.getIn(carMakeStorePath)) {
        const vehicleMakeLabel = vehicleMake({ state });
        return formPayload.setIn(carMakeStorePath, vehicleMakeLabel);
    }
    return formPayload;
};

/**
 * Get the call centre opening times based on daylight saving
 * It currently returns times based on either AEDT or AEST.
 * @param {Immutable.Map} state - The application state
 * @param {Boolean} isDaylightSavingTimeObserved - True if daylight saving time observed
 * @returns {Object} Returns an object of OpenTime objects for each vertical.
 */
export const getCallCentreOpenHours = ({ state }, isDaylightSavingTimeObserved) => {
    const times = state.getIn(['vertical', 'callCentreHours', 'times']);
    const isDST = (hours) => hours.timeZone === (isDaylightSavingTimeObserved ? 'AEDT' : 'AEST');
    return Object.keys(times).reduce((a, c) => ({ ...a, [c]: times[c].find(isDST) }), {});
};
